'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

/* ─── Animated Counter ─────────────────────────────────────────── */
function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const step = Math.ceil(target / 80);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(start);
          }, 18);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Data ─────────────────────────────────────────────────────── */
const stats = [
  { value: 500, suffix: '+', label: 'Vehicles Deployed', sub: 'Across Odisha' },
  { value: 12000, suffix: '+', label: 'Trips Completed', sub: 'MCL, NALCO & Civil' },
  { value: 15, suffix: '+', label: 'Years of Service', sub: 'Est. in Angul, Odisha' },
  { value: 50, suffix: '+', label: 'Active Contracts', sub: 'Government & Private' },
];

const services = [
  {
    id: 's1',
    emoji: '🚌',
    title: 'Bus Hiring for MCL & NALCO',
    desc: 'Regular-basis bus hiring for Mahanadi Coalfields Limited and NALCO operations. Non-deluxe and deluxe fleet available for daily employee transport covering 1,900+ km routes.',
    tag: 'Government Contracts',
    accent: '#1e40af',
    bg: 'rgba(30,64,175,0.07)',
    span: 'lg:col-span-2 lg:row-span-1',
  },
  {
    id: 's2',
    emoji: '🏗️',
    title: 'Civil & PWD Project Transport',
    desc: 'Dedicated fleet for civil construction projects, PWD contracts, and infrastructure work across Odisha. Reliable logistics for project sites.',
    tag: 'Civil Projects',
    accent: '#d97706',
    bg: 'rgba(217,119,6,0.07)',
    span: 'lg:col-span-1 lg:row-span-1',
  },
  {
    id: 's3',
    emoji: '🚐',
    title: 'Staff & Employee Shuttles',
    desc: 'Scheduled employee pick-up and drop services for industrial plants, mines, and corporate offices. Punctual, safe, and cost-effective.',
    tag: 'Corporate',
    accent: '#16a34a',
    bg: 'rgba(22,163,74,0.07)',
    span: 'lg:col-span-1 lg:row-span-1',
  },
  {
    id: 's4',
    emoji: '📋',
    title: 'GST-Compliant Invoicing',
    desc: 'All trips backed by proper GST invoices, e-way bills, and compliance documentation. Fully auditable records for government tenders.',
    tag: 'Compliance',
    accent: '#0284c7',
    bg: 'rgba(2,132,199,0.07)',
    span: 'lg:col-span-1 lg:row-span-1',
  },
  {
    id: 's5',
    emoji: '🛡️',
    title: 'Vehicle Compliance & Safety',
    desc: 'All vehicles maintained with valid fitness certificates, insurance, permits, and PUC. Zero-tolerance safety policy across the entire fleet.',
    tag: 'Safety',
    accent: '#7c3aed',
    bg: 'rgba(124,58,237,0.07)',
    span: 'lg:col-span-2 lg:row-span-1',
  },
];

const clients = [
  { name: 'Mahanadi Coalfields Ltd.', short: 'MCL', color: '#1e40af' },
  { name: 'NALCO', short: 'NALCO', color: '#d97706' },
  { name: 'PWD Odisha', short: 'PWD', color: '#16a34a' },
  { name: 'Civil Contractors', short: 'CIVIL', color: '#0284c7' },
  { name: 'Private Sector', short: 'PVT', color: '#7c3aed' },
];

const milestones = [
  { year: '2008', event: 'Founded in Angul, Odisha with a small fleet of 10 vehicles' },
  { year: '2012', event: 'First government contract with Mahanadi Coalfields Limited (MCL)' },
  { year: '2016', event: 'Expanded to NALCO operations and Civil/PWD contracts' },
  { year: '2020', event: 'Fleet crossed 200+ vehicles; GST registration and compliance upgrade' },
  { year: '2024', event: 'Awarded L1 rank in MCL bus hiring tender — ₹24.3 Lakh contract' },
  { year: '2026', event: 'Digital fleet management platform launched; 500+ vehicles tracked' },
];

/* ─── Page ─────────────────────────────────────────────────────── */
export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(id);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans overflow-x-hidden">

      {/* ══════════════════════════════════════════════════════════
          STICKY NAV
      ══════════════════════════════════════════════════════════ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-[0_1px_24px_rgba(0,0,0,0.08)] border-b border-[#e2e8f0]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-3 group">
            <div className="relative w-10 h-10 rounded-xl overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-200">
              <Image
                src="/assets/dehuri-travels-logo.svg"
                alt="Dehuri Travels Logo"
                fill
                className="object-contain p-1"
              />
            </div>
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] leading-none transition-colors duration-300 ${scrolled ? 'text-[#64748b]' : 'text-white/60'}`}>
                Est. 2008 · Angul, Odisha
              </p>
              <p className={`text-[17px] font-bold leading-tight tracking-tight transition-colors duration-300 ${scrolled ? 'text-[#0f172a]' : 'text-white'}`}>
                Dehuri Travels
              </p>
            </div>
          </button>

          {/* Nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {[
              { id: 'hero', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'about', label: 'About Us' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeSection === item.id
                    ? scrolled ? 'bg-[#1e40af]/10 text-[#1e40af]' : 'bg-white/15 text-white'
                    : scrolled ? 'text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9]' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <Link
            href="/sign-up-login-screen"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#f59e0b] text-white text-sm font-bold shadow-lg hover:bg-[#d97706] hover:shadow-xl hover:scale-[1.03] transition-all duration-200"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            Login
          </Link>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════
          PAGE 1 — HERO
      ══════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-screen flex flex-col overflow-hidden">
        {/* Multi-layer background */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0c1e4a 0%, #1e3a8a 40%, #1e40af 70%, #1d4ed8 100%)' }} />
        {/* Animated orbs */}
        <div className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)', animation: 'pulse 8s ease-in-out infinite' }} />
        <div className="absolute bottom-[-15%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #60a5fa 0%, transparent 70%)', animation: 'pulse 10s ease-in-out infinite 2s' }} />
        {/* Road texture lines */}
        <div className="absolute inset-0 overflow-hidden opacity-[0.04]">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="absolute h-px bg-white" style={{ top: `${12 + i * 12}%`, left: 0, right: 0, transform: `rotate(${i % 2 === 0 ? 0.3 : -0.3}deg)` }} />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-20 flex-1 flex flex-col justify-center">
          <div className="grid lg:grid-cols-[1fr_420px] gap-16 items-center">
            {/* Left — copy */}
            <div>
              {/* Badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-8 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-[#f59e0b] animate-pulse" />
                <span className="text-xs font-bold text-white/80 tracking-widest uppercase">Odisha's Trusted Transport Partner</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6">
                Moving Odisha<br />
                <span className="relative">
                  <span className="text-[#f59e0b]">Forward.</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#f59e0b]/40 rounded-full" />
                </span>
              </h1>

              <p className="text-lg md:text-xl text-white/65 leading-relaxed mb-10 max-w-2xl">
                Dehuri Travels — headquartered in Angul, Odisha — provides government-grade bus hiring, employee shuttles, and fleet logistics for MCL, NALCO, and Civil contractors since 2008.
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <Link
                  href="/sign-up-login-screen"
                  className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-[#f59e0b] text-white font-bold text-base shadow-2xl hover:bg-[#d97706] hover:scale-[1.03] transition-all duration-200"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                  </svg>
                  Access Dashboard
                </Link>
                <button
                  onClick={() => scrollTo('services')}
                  className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-white/10 border border-white/20 text-white font-semibold text-base backdrop-blur-sm hover:bg-white/15 transition-all duration-200"
                >
                  Our Services
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/>
                  </svg>
                </button>
              </div>

              {/* Client logos row */}
              <div>
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-4">Trusted by</p>
                <div className="flex flex-wrap gap-3">
                  {clients.map((c) => (
                    <div
                      key={c.name}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/8 border border-white/12 backdrop-blur-sm"
                    >
                      <div className="w-6 h-6 rounded-md flex items-center justify-center text-[9px] font-black text-white"
                        style={{ backgroundColor: c.color }}>
                        {c.short.slice(0, 1)}
                      </div>
                      <span className="text-xs font-semibold text-white/70">{c.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — stats card */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Glow */}
                <div className="absolute inset-0 rounded-3xl blur-2xl opacity-30" style={{ background: 'linear-gradient(135deg, #f59e0b, #1e40af)' }} />
                <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl bg-[#f59e0b] flex items-center justify-center shadow-lg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white/50 uppercase tracking-widest">Fleet Overview</p>
                      <p className="text-base font-bold text-white">Dehuri Travels</p>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#16a34a]/20 border border-[#16a34a]/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a] animate-pulse" />
                      <span className="text-[10px] font-bold text-[#4ade80]">ACTIVE</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {stats.map((s, i) => (
                      <div key={i} className="bg-white/8 rounded-2xl p-4 border border-white/10">
                        <p className="text-3xl font-bold text-white tabular-nums">
                          <AnimatedCounter target={s.value} suffix={s.suffix} />
                        </p>
                        <p className="text-xs font-semibold text-white/70 mt-1">{s.label}</p>
                        <p className="text-[10px] text-white/40 mt-0.5">{s.sub}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-white/50 uppercase tracking-widest">GST Registration</span>
                      <span className="text-xs font-bold text-[#f59e0b]">21AAJFD1932F1ZZ</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white/50 uppercase tracking-widest">Headquarters</span>
                      <span className="text-xs font-bold text-white/70">Angul, Odisha 759148</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 flex justify-center pb-10">
          <button onClick={() => scrollTo('services')} className="flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors duration-200">
            <span className="text-[10px] font-bold uppercase tracking-widest">Scroll</span>
            <div className="w-5 h-8 rounded-full border border-white/30 flex items-start justify-center pt-1.5">
              <div className="w-1 h-2 rounded-full bg-white/60" style={{ animation: 'scrollBounce 2s ease-in-out infinite' }} />
            </div>
          </button>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          STATS BAND
      ══════════════════════════════════════════════════════════ */}
      <div className="bg-[#0f172a] border-y border-[#1e293b]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl md:text-5xl font-bold tabular-nums" style={{ color: i % 2 === 0 ? '#f59e0b' : '#60a5fa' }}>
                  <AnimatedCounter target={s.value} suffix={s.suffix} />
                </p>
                <p className="text-sm font-bold text-white mt-2">{s.label}</p>
                <p className="text-xs text-[#64748b] mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          PAGE 2 — SERVICES
      ══════════════════════════════════════════════════════════ */}
      <section id="services" className="py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section header — asymmetric */}
          <div className="grid lg:grid-cols-[1fr_1fr] gap-12 items-end mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1e40af]/10 border border-[#1e40af]/20 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1e40af]" />
                <span className="text-xs font-bold text-[#1e40af] uppercase tracking-widest">What We Do</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0f172a] leading-[1.1] tracking-tight">
                Transport solutions<br />
                <span className="text-[#1e40af]">built for Odisha.</span>
              </h2>
            </div>
            <div className="lg:pb-2">
              <p className="text-lg text-[#64748b] leading-relaxed">
                From daily employee shuttles for coal mines to GST-compliant invoicing for government tenders — Dehuri Travels handles the full spectrum of industrial transport in Odisha.
              </p>
            </div>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div
                key={s.id}
                className={`${s.span} group relative rounded-3xl border border-[#e2e8f0] bg-white p-8 overflow-hidden hover:shadow-xl hover:border-transparent transition-all duration-400 cursor-default`}
                style={{ '--hover-border': s.accent } as React.CSSProperties}
              >
                {/* Background accent blob */}
                <div
                  className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(circle, ${s.accent}20 0%, transparent 70%)` }}
                />

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-sm"
                      style={{ backgroundColor: s.bg }}
                    >
                      {s.emoji}
                    </div>
                    <span
                      className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: s.bg, color: s.accent }}
                    >
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0f172a] mb-3 leading-snug">{s.title}</h3>
                  <p className="text-sm text-[#64748b] leading-relaxed">{s.desc}</p>

                  <div className="mt-6 flex items-center gap-2 text-xs font-bold" style={{ color: s.accent }}>
                    <span>Learn more</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform duration-200">
                      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA strip */}
          <div className="mt-14 relative overflow-hidden rounded-3xl p-10 md:p-14" style={{ background: 'linear-gradient(135deg, #0c1e4a 0%, #1e3a8a 50%, #1d4ed8 100%)' }}>
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #f59e0b, transparent 70%)' }} />
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-3">Ready to partner with us?</p>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-2">
                  Government-grade transport.<br />
                  <span className="text-[#f59e0b]">Delivered on time.</span>
                </h3>
                <p className="text-white/60 text-base mt-3 max-w-lg">
                  Dehuri Travels has been awarded L1 rank in MCL tenders. Our fleet is fully compliant, GST-registered, and ready for your next contract.
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-center gap-3">
                <Link
                  href="/sign-up-login-screen"
                  className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-[#f59e0b] text-white font-bold text-lg shadow-2xl hover:bg-[#d97706] hover:scale-[1.04] transition-all duration-200 whitespace-nowrap"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                  </svg>
                  Login to Dashboard
                </Link>
                <p className="text-white/35 text-xs">Admin · Staff · Secure Access</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          PAGE 3 — ABOUT US
      ══════════════════════════════════════════════════════════ */}
      <section id="about" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          {/* Section header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f59e0b]/10 border border-[#f59e0b]/25 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]" />
              <span className="text-xs font-bold text-[#d97706] uppercase tracking-widest">Our Story</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0f172a] leading-[1.1] tracking-tight mb-5">
              About Dehuri Travels
            </h2>
            <p className="text-lg text-[#64748b] max-w-2xl mx-auto leading-relaxed">
              A government-registered transport company from Angul, Odisha — serving India's largest coal and aluminium industries with a reliable, compliant fleet since 2008.
            </p>
          </div>

          {/* About content — asymmetric 60/40 */}
          <div className="grid lg:grid-cols-[3fr_2fr] gap-16 items-start mb-24">
            <div>
              <div className="prose prose-lg max-w-none">
                <p className="text-[#334155] leading-relaxed text-base mb-6">
                  <strong className="text-[#0f172a]">Dehuri Travels</strong> is a legally registered transport enterprise headquartered at NS Nagar, Bharatpur, Angul, Odisha — PIN 759148. The company holds a valid LEI (Legal Entity Identifier: 8945001J4AX2KLNVQW88) and GST registration (21AAJFD1932F1ZZ), making it fully compliant for government tenders and large-scale contracts.
                </p>
                <p className="text-[#334155] leading-relaxed text-base mb-6">
                  We specialize in <strong className="text-[#1e40af]">bus hiring services for Mahanadi Coalfields Limited (MCL)</strong> — a Ministry of Coal enterprise — providing regular-basis local transport covering 1,900+ km routes. In 2024, Dehuri Travels was awarded the <strong className="text-[#d97706]">L1 (lowest bidder, best value) rank</strong> in the MCL bus hiring tender with a contract value of ₹24,30,608, beating 8 other qualified competitors.
                </p>
                <p className="text-[#334155] leading-relaxed text-base mb-6">
                  Beyond MCL, our fleet serves <strong className="text-[#0f172a]">NALCO (National Aluminium Company)</strong>, PWD and Civil contractors, and private sector logistics across Odisha. Every vehicle in our fleet maintains valid fitness certificates, insurance, permits, and PUC documentation — zero compromise on safety or compliance.
                </p>
                <p className="text-[#334155] leading-relaxed text-base">
                  Our digital fleet management platform — launched in 2026 — enables real-time vehicle tracking, GST-compliant invoicing, driver management, and automated compliance alerts, bringing enterprise-grade operations to Odisha's transport sector.
                </p>
              </div>
            </div>

            {/* Info card */}
            <div className="space-y-4">
              {[
                { label: 'Legal Name', value: 'DEHURI TRAVELS', icon: '🏢' },
                { label: 'Headquarters', value: 'NS Nagar, Bharatpur, Angul, Odisha 759148', icon: '📍' },
                { label: 'GST Number', value: '21AAJFD1932F1ZZ', icon: '📋' },
                { label: 'LEI Number', value: '8945001J4AX2KLNVQW88', icon: '🔖' },
                { label: 'Entity Status', value: 'ACTIVE — Fully Corroborated', icon: '✅' },
                { label: 'Primary Sector', value: 'Government Transport Contracts', icon: '🚌' },
                { label: 'Key Clients', value: 'MCL, NALCO, PWD Odisha', icon: '🤝' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4 p-4 rounded-2xl bg-[#f8fafc] border border-[#e2e8f0] hover:border-[#cbd5e1] transition-colors duration-200">
                  <span className="text-xl mt-0.5">{item.icon}</span>
                  <div>
                    <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-widest mb-0.5">{item.label}</p>
                    <p className="text-sm font-semibold text-[#0f172a]">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline — horizontal scroll on mobile, grid on desktop */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-10">
              <div className="h-px flex-1 bg-[#e2e8f0]" />
              <h3 className="text-xl font-bold text-[#0f172a] px-4">Our Journey</h3>
              <div className="h-px flex-1 bg-[#e2e8f0]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className="relative p-6 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] hover:bg-white hover:shadow-md hover:border-[#cbd5e1] transition-all duration-300"
                >
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white font-black text-sm mb-4 shadow-md"
                    style={{ background: i % 3 === 0 ? '#1e40af' : i % 3 === 1 ? '#d97706' : '#16a34a' }}
                  >
                    {m.year.slice(2)}
                  </div>
                  <p className="text-lg font-bold text-[#0f172a] mb-1">{m.year}</p>
                  <p className="text-sm text-[#64748b] leading-relaxed">{m.event}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why choose us — 4-tile asymmetric */}
          <div className="rounded-3xl overflow-hidden border border-[#e2e8f0]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[#e2e8f0]">
              {[
                { icon: '🏆', title: 'L1 Ranked Bidder', desc: 'Awarded best-value rank in MCL government tenders — proven cost efficiency.' },
                { icon: '📜', title: 'Fully GST Compliant', desc: 'GST-registered with proper invoicing, e-way bills, and quarterly filings.' },
                { icon: '🛡️', title: 'Zero Safety Compromise', desc: 'All vehicles hold valid fitness, insurance, and PUC certificates at all times.' },
                { icon: '⚡', title: 'Digital-First Operations', desc: 'Real-time fleet tracking, digital driver records, and automated compliance alerts.' },
              ].map((item) => (
                <div key={item.title} className="p-8 bg-white hover:bg-[#f8fafc] transition-colors duration-200">
                  <span className="text-3xl block mb-4">{item.icon}</span>
                  <h4 className="text-base font-bold text-[#0f172a] mb-2">{item.title}</h4>
                  <p className="text-sm text-[#64748b] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #0c1e4a 0%, #1e3a8a 50%, #1d4ed8 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-3xl bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-8 shadow-2xl">
            <Image
              src="/assets/dehuri-travels-logo.svg"
              alt="Dehuri Travels"
              width={48}
              height={48}
              className="object-contain"
            />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-[1.1] tracking-tight mb-5">
            Manage your fleet.<br />
            <span className="text-[#f59e0b]">Grow your contracts.</span>
          </h2>
          <p className="text-lg text-white/60 mb-10 max-w-xl mx-auto leading-relaxed">
            Log in to the Dehuri Travels platform — track vehicles, generate GST invoices, manage drivers, and stay compliant. Built for Odisha's transport industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/sign-up-login-screen"
              className="flex items-center justify-center gap-3 px-10 py-4 rounded-2xl bg-[#f59e0b] text-white font-bold text-lg shadow-2xl hover:bg-[#d97706] hover:scale-[1.03] transition-all duration-200"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
              Admin Login
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="flex items-center justify-center gap-3 px-10 py-4 rounded-2xl bg-white/10 border border-white/25 text-white font-bold text-lg backdrop-blur-sm hover:bg-white/15 transition-all duration-200"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              Staff Login
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════════ */}
      <footer className="bg-[#0f172a] border-t border-[#1e293b]">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center overflow-hidden">
                <Image src="/assets/dehuri-travels-logo.svg" alt="Dehuri Travels" width={28} height={28} className="object-contain p-0.5" />
              </div>
              <div>
                <p className="text-sm font-bold text-white">Dehuri Travels</p>
                <p className="text-xs text-[#475569]">NS Nagar, Angul, Odisha 759148</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              {[
                { label: 'Home', id: 'hero' },
                { label: 'Services', id: 'services' },
                { label: 'About', id: 'about' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="text-sm text-[#64748b] hover:text-white transition-colors duration-150 font-medium"
                >
                  {item.label}
                </button>
              ))}
              <Link href="/sign-up-login-screen" className="text-sm text-[#f59e0b] hover:text-[#fbbf24] transition-colors duration-150 font-bold">
                Login →
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#1e293b] flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-[#475569]">GST: 21AAJFD1932F1ZZ · LEI: 8945001J4AX2KLNVQW88</p>
            <p className="text-xs text-[#475569]">© 2026 Dehuri Travels. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(6px); opacity: 0.4; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
